/**
 * WYSIWYG Render Module
 * Manages the content images list UI rendering.
 */

import { Patterns } from "../constants/patterns";
import { phpUrl } from "../core/php-data-access";
import { Roles } from "../constants/roles";
import { CommonUtils } from "../util/common-utils";
import { WysiwygListener } from "./wysiwyg-listener";
import { WysiwygForm } from "./wysiwyg-form";

interface FilePickerOptions {
    accept: string;
    multiple?: boolean;
}

interface QuillRangeLike {
    index: number;
}

interface QuillClipboardLike {
    dangerouslyPasteHTML(index: number, html: string, source?: string): void;
}

interface QuillLike {
    getSelection(focus?: boolean): QuillRangeLike | null;
    getLength(): number;
    insertEmbed(index: number, type: string, value: string, source?: string): void;
    setSelection(index: number, length: number, source?: string): void;
    clipboard: QuillClipboardLike;
}

class WysiwygRender {
    public boardId: string;

    public userId: number | null | undefined;

    public contentImages: string[];

    public constructor(boardId: string, userId: number | null | undefined) {
        this.boardId = boardId;
        this.userId = userId;
        this.contentImages = [];
    }

    public static openTransientFilePicker({ accept, multiple = false }: FilePickerOptions): Promise<FileList | null> {
        return new Promise((resolve) => {
            const pickerRole = `wysiwyg-file-picker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const safeAccept = CommonUtils.escapeHtmlAttribute(String(accept || ""));
            const safeRole = CommonUtils.escapeHtmlAttribute(pickerRole);
            const multipleAttr = multiple ? " multiple" : "";

            document.body.insertAdjacentHTML(
                "beforeend",
                `<input data-role="${safeRole}" type="file" accept="${safeAccept}"${multipleAttr} style="display:none;">`,
            );

            const input = WysiwygForm.getTransientFileInput(pickerRole);
            if (!(input instanceof HTMLInputElement)) {
                resolve(null);
                return;
            }

            WysiwygListener.bindTransientFilePickerChange(input, () => {
                const files = input.files;
                input.remove();
                resolve(files && files.length ? files : null);
            });

            input.click();
        });
    }

    public static buildContentImageItemHtml(filename: string): string {
        const safeText = CommonUtils.escapeHtml(String(filename || ""));
        const safeAttr = CommonUtils.escapeHtmlAttribute(String(filename || ""));

        return `
            <li data-filename="${safeAttr}">
                <span class="filename">${safeText}</span>
                <button type="button" class="btn-delete-content-image" data-filename="${safeAttr}"></button>
            </li>
        `;
    }

    public static renderSnsLinksList(snsLinksList: HTMLElement | null | undefined, links: string[]): void {
        if (!(snsLinksList instanceof HTMLElement)) return;

        const items = Array.isArray(links) ? links : [];
        const html = items
            .map((linkUrl, index) => {
                const safeIndex = CommonUtils.escapeHtmlAttribute(String(index));
                const safeValue = CommonUtils.escapeHtmlAttribute(String(linkUrl || ""));
                return `
                    <div class="sns-link-item" data-role="${Roles.SNS_LINK_ITEM}" data-index="${safeIndex}">
                        <input type="text" name="sns_links[]" class="textbox sns-link-input" data-role="${Roles.SNS_LINK_INPUT}" value="${safeValue}" placeholder="https://...">
                    </div>
                `;
            })
            .join("");

        snsLinksList.innerHTML = html;
    }

    public static renderHiddenSnsLinkInputs(form: HTMLFormElement | null | undefined, links: string[]): void {
        if (!(form instanceof HTMLFormElement)) return;

        const items = Array.isArray(links) ? links : [];
        if (items.length === 0) return;

        const html = items
            .map((link) => {
                const safeValue = CommonUtils.escapeHtmlAttribute(String(link || ""));
                return `<input type="hidden" name="sns_links[]" value="${safeValue}" data-role="${Roles.SNS_LINK_INPUT}" data-sns-links-origin="auto">`;
            })
            .join("");

        form.insertAdjacentHTML("beforeend", html);
    }

    public trackContentImage(filename: string): void {
        if (!this.contentImages.includes(filename)) {
            this.contentImages.push(filename);
            this.updateContentImagesList();
        }
    }

    public removeContentImage(filename: string): void {
        this.contentImages = this.contentImages.filter((item) => item !== filename);
        this.updateContentImagesList();
    }

    public updateContentImagesList(): void {
        const listElement = WysiwygForm.getContentImagesList(this.boardId, document);

        if (!(listElement instanceof HTMLElement)) {
            return;
        }

        listElement.innerHTML = "";

        if (!this.contentImages || this.contentImages.length === 0) {
            return;
        }

        listElement.innerHTML = this.contentImages
            .map((filename) => WysiwygRender.buildContentImageItemHtml(filename))
            .join("");
    }

    public extractExistingContentImages(editorRoot: HTMLElement): void {
        const images = WysiwygForm.getImages(editorRoot);
        const pattern = Patterns.createPostEditorImageFilenamePattern(
            this.boardId,
            this.userId,
            phpUrl.base.postImages(),
        );

        this.contentImages = [];

        images.forEach((img) => {
            const match = img.src.match(pattern);
            if (match && match[1]) {
                this.contentImages.push(match[1]);
            }
        });
    }

    public getContentImages(): string[] {
        return [...this.contentImages];
    }

    public static insertVideoByUrl(quill: QuillLike, url: string): void {
        if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) {
            WysiwygRender.insertVideoTag(quill, url);
            return;
        }

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, "video", url, "user");
        quill.setSelection(index + 1, 0, "user");
    }

    public static insertVideoTag(quill: QuillLike, url: string): void {
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        const html = `<div class="ql-video-wrapper"><video controls preload="metadata" style="max-width:100%;height:auto;display:block;" src="${encodeURI(url)}"></video></div>`;

        quill.clipboard.dangerouslyPasteHTML(index, html, "user");
        quill.setSelection(index + 1, 0, "user");
    }

    public static mountEditorForTextarea(textarea: HTMLTextAreaElement | null | undefined): HTMLElement | null {
        if (!textarea || !textarea.parentElement) {
            return null;
        }

        const previousSibling = textarea.previousElementSibling;
        if (previousSibling instanceof HTMLElement && previousSibling.dataset.role === Roles.WYSIWYG_EDITOR) {
            return previousSibling;
        }

        const mountId = `wysiwyg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const html = `<div class="wysiwyg-editor" data-role="${Roles.WYSIWYG_EDITOR}" data-wysiwyg-mount-id="${mountId}"></div>`;
        textarea.insertAdjacentHTML("beforebegin", html);

        const mounted = WysiwygForm.getMountedEditor(textarea.parentElement, mountId);
        return mounted instanceof HTMLElement ? mounted : null;
    }

    public static enableTextareaFallback(textarea: HTMLTextAreaElement | null | undefined): void {
        if (!textarea) return;
        textarea.classList.add("wysiwyg-fallback");
    }
}

export { WysiwygRender };
